import React from "react";
import Message from "../../../i18n/Message";
import useSubscriptionGate from "./useSubscriptionGate";

import "./subscription.scss";

/**
 * Two teaser treatments for plan-locked / read-only surfaces:
 *  - variant "inline": a small "Upgrade" link, meant to sit next to a disabled control.
 *  - variant "blur": renders `children` blurred behind a lock overlay with an upgrade CTA,
 *    for whole-feature areas (e.g. Live Chat analytics/settings on Lite).
 *
 * Plan resolution mirrors GatedButton: when the app is read-only (trial ended / paid lapsed)
 * the hint opens the payment popup for the user's *own* plan (openUpgrade with no arg -> the
 * server-provided sub.planCode). Only a genuine feature upsell (shown while not read-only)
 * targets an explicit `plan` (e.g. "pro"). `plan` intentionally has no default so an omitted
 * or `undefined` prop resolves to the current plan rather than a hard-coded "pro".
 *
 * Enterprise is the top tier with no higher self-serve plan: when an enterprise customer reaches
 * one of their individually-negotiated limits the hint links to the contact page instead of
 * opening checkout (regardless of the caller's `plan`/`ctaId`).
 */
const UpgradeHint = ({
  variant = "inline",
  messageId = "upgrade_hint_feature",
  titleId,
  ctaId = "upgrade_to_pro",
  plan,
  className = "",
  children,
}) => {
  const { openUpgrade, isReadOnly, isEnterprise, contactSalesUrl } =
    useSubscriptionGate();
  const enterpriseContact = !isReadOnly && isEnterprise;
  const onUpgrade = () => openUpgrade(isReadOnly ? undefined : plan);

  if (variant === "blur") {
    return (
      <div className={"upgrade_teaser " + className}>
        <div className="upgrade_teaser_content" aria-hidden="true">
          {children}
        </div>
        <div className="upgrade_teaser_overlay">
          <div className="upgrade_teaser_box">
            {titleId && (
              <div className="upgrade_teaser_title">
                <Message id={titleId} />
              </div>
            )}
            <div className="upgrade_teaser_text">
              <Message
                id={enterpriseContact ? "upgrade_hint_enterprise_limit" : messageId}
              />
            </div>
            {enterpriseContact ? (
              <a
                className="btn btn-dark upgrade_teaser_btn"
                href={contactSalesUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Message id="contact_sales_cta" />
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-dark upgrade_teaser_btn"
                onClick={onUpgrade}
              >
                <Message id={ctaId} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (enterpriseContact) {
    return (
      <a
        className={"upgrade_hint " + className}
        href={contactSalesUrl}
        target="_blank"
        rel="noreferrer"
      >
        <Message id="upgrade_hint_enterprise_limit" />
      </a>
    );
  }

  return (
    <span
      className={"upgrade_hint " + className}
      role="button"
      tabIndex={0}
      onClick={onUpgrade}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onUpgrade();
      }}
    >
      <Message id={messageId} />
    </span>
  );
};

export default UpgradeHint;
